import qs from 'qs';
import { MouseEvent } from 'react';
import { Navigate, NavLink, Route } from 'react-router-dom-v5-compat';

import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import {
  PageTabBar,
  TabbedSectionContainer,
  TabView,
} from 'dashboard/components/TabbedSectionV2/shared';
import { Tab } from 'design-system/components/TabBar';

interface RoutedTabView extends TabView {
  /**
   * The partial URI for the tab that will be appended onto the current
   * location for convenience.
   */
  path: string;
  /**
   * An optional mapping of key-value pairs for query parameters. These query parameters
   * are stringified and appended to the corresponding path for the tab URI. Note
   * that query parameters do not affect the corresponding route.
   */
  queryParams?: Record<string, string>;
  /**
   * Allows tab view children to themselves have nested routes.
   */
  exact?: boolean;
}

interface RoutedTabbedSectionProps {
  tabs: Array<RoutedTabView | false | undefined | null>;
  dataTest?: string;
}

const leadingSlashRegex = /^\//;
const trailingSplatRegex = /\/\*$/;

const RoutedTabbedSection = ({ tabs, dataTest }: RoutedTabbedSectionProps) => {
  const normalizedTabs = tabs.map((t) => {
    if (!t || typeof t !== 'object') {
      return t;
    }

    let path = t.path.replace(leadingSlashRegex, '');

    if (t.exact === false) {
      path += '/*';
    }

    return {
      ...t,
      path,
    };
  });

  return (
    <TabbedSectionContainer data-test={dataTest}>
      <RoutedTabbedSectionCard tabs={normalizedTabs} />
      <RoutedTabbedSectionSwitch tabs={normalizedTabs} />
    </TabbedSectionContainer>
  );
};

export type RoutedTabbedSectionCardChild = Pick<
  RoutedTabView,
  'path' | 'name' | 'dataTest' | 'disabled' | 'exact' | 'badge' | 'queryParams'
>;

interface RoutedTabbedSectionCardProps {
  tabs: Array<RoutedTabbedSectionCardChild | false | undefined | null>;
  browserPath?: string;
}

// Use these separate components if you need more flexibility and want to
// render the card and the children separately. You will need to path in the
// base paths.
export const RoutedTabbedSectionCard = ({ tabs }: RoutedTabbedSectionCardProps) => {
  const existingTabs = tabs.filter(Boolean) as RoutedTabbedSectionCardChild[];

  if (existingTabs.length <= 1) {
    return null;
  }

  return (
    <PageTabBar>
      {existingTabs.map(
        ({ path, name, disabled, dataTest: tabDataTest, exact = true, badge, queryParams }) => {
          return (
            <Tab
              key={path}
              disabled={disabled}
              onClick={(e: MouseEvent) => {
                disabled && e.preventDefault();
              }}
              to={{
                pathname: path === '/' ? '.' : path.replace(trailingSplatRegex, ''),
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                search: queryParams ? `?${qs.stringify(queryParams)}` : null,
              }}
              data-test={tabDataTest}
              as={NavLink}
              end={exact}
              badge={badge}
            >
              {name}
            </Tab>
          );
        },
      )}
    </PageTabBar>
  );
};

type RoutedTabbedSectionSwitchChild = Pick<
  RoutedTabView,
  'path' | 'contents' | 'exact' | 'disabled'
>;

interface RoutedTabbedSectionSwitchProps {
  tabs: Array<RoutedTabbedSectionSwitchChild | false | undefined | null>;
  browserPath?: string;
  routePath?: string;
}

export const RoutedTabbedSectionSwitch = ({ tabs }: RoutedTabbedSectionSwitchProps) => {
  const existingTabs = tabs.filter(Boolean) as RoutedTabbedSectionSwitchChild[];
  const hasExplicitRootPath = existingTabs.some(({ path }) => path === '/');
  const firstEnabledTabIndex = existingTabs.findIndex(({ disabled }) => !disabled);

  return (
    <SentryRoutes>
      {existingTabs.map(({ path, contents }, index) => (
        <Route
          key={path}
          path={path}
          element={contents}
          index={index === firstEnabledTabIndex && !hasExplicitRootPath}
        />
      ))}
      <Route
        path='*'
        element={
          <Navigate
            to={
              hasExplicitRootPath
                ? '.'
                : existingTabs[firstEnabledTabIndex].path.replace(trailingSplatRegex, '')
            }
            replace
          />
        }
      />
    </SentryRoutes>
  );
};

export default RoutedTabbedSection;
